/**
 * @file ProducerCertFields.tsx
 * @description Поля окна сертификата производителя (phase19.31; phase19.16,
 * партия «Юридические документы», шаг В — uiux-legal-steps-b-c §6.3): вид
 * документа (`SelectField`), номер, кем выдан, даты (`DateField`, «Пусто —
 * бессрочный»), «Показывать на сайте» с подписью, где именно, и подсказка о
 * сроке сразу при вводе даты (`certificateExpiryHint`).
 */

import { Label } from "../../ui/label";
import { DateField } from "../../ui/DateField";
import { Input } from "../../ui/input";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { SelectField } from "../../ui/SelectField";
import { Switch } from "../../ui/switch";
import type { CertificateType } from "../../../lib/api-catalog";
import { CERT_LABELS } from "../../../lib/catalog-format";
import { certificateExpiryHint } from "../../../lib/certificate-status";

/** Поля формы сертификата (даты — `YYYY-MM-DD`, пусто — нет). */
export interface CertForm {
  type: CertificateType;
  number: string;
  issuedBy: string;
  issuedAt: string;
  validUntil: string;
  isPublic: boolean;
}

const TYPE_OPTIONS = (Object.keys(CERT_LABELS) as CertificateType[]).map((value) => ({ value, label: CERT_LABELS[value] }));

interface Props {
  value: CertForm;
  onChange: (next: CertForm) => void;
  disabled?: boolean;
}

export function ProducerCertFields({ value, onChange, disabled }: Props) {
  const set = <K extends keyof CertForm>(key: K, v: CertForm[K]) => onChange({ ...value, [key]: v });
  const expiry = certificateExpiryHint(value.validUntil);
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ct-type">Вид документа</Label>
        <SelectField id="ct-type" ariaLabel="Вид документа" value={value.type} onChange={(v) => set("type", v as CertificateType)} options={TYPE_OPTIONS} disabled={disabled} />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ct-num">Номер *</Label>
        <Input id="ct-num" value={value.number} onChange={(e) => set("number", e.target.value)} disabled={disabled} required />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ct-issuer">Кем выдан</Label>
        <Input id="ct-issuer" value={value.issuedBy} onChange={(e) => set("issuedBy", e.target.value)} disabled={disabled} />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor="ct-from">Дата выдачи</Label>
          <DateField id="ct-from" value={value.issuedAt} onChange={(v) => set("issuedAt", v)} disabled={disabled} title="Дата выдачи" />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor="ct-until">Действует до</Label>
          <DateField id="ct-until" value={value.validUntil} onChange={(v) => set("validUntil", v)} disabled={disabled} title="Действует до" />
          <span className="type-caption text-on-surface-variant">Пусто — бессрочный (например, СГР)</span>
        </div>
      </div>
      <label className="flex min-h-12 cursor-pointer items-center gap-3">
        <Switch checked={value.isPublic} onCheckedChange={(c) => set("isPublic", c === true)} disabled={disabled} />
        <span className="flex flex-col">
          <span className="type-body text-on-surface">Показывать на сайте</span>
          <span className="type-caption text-on-surface-variant">Карточка товара и страница «Сертификаты и декларации»</span>
        </span>
      </label>
      {expiry ? <NoticeStrip tone={expiry.tone}>{expiry.text}</NoticeStrip> : null}
    </>
  );
}
